import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useVibesites, EditableSlotGroup, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, escapeHtml, fieldToken, noPeriod, paragraphs, splitBlurb, galleryPhotos } from '../utils/quinn';
import { useRawProperty } from '../utils/hooks';
import { byDist, nearbySiblings, type Poi } from '../utils/places';
import { photoTiles, placeTiles } from '../utils/gallery';

const photoDef: SlotGroupDefinition = {
  label: 'Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'property_featured_image_media.alt_text', defaultValue: '' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// `photo` strategies other than "featured" report their own definition so the editor shows the real source.
const amenityPhotoDef: SlotGroupDefinition = {
  label: 'Photo',
  description: 'Until a photo is picked, shows a pool or clubhouse photo from the gallery, else the featured image.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: '' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const parkPhotoDef: SlotGroupDefinition = {
  label: 'Photo',
  description: 'Until a place is picked, shows the nearest park or green space with a photo. A place photo is captioned with its name and distance.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[0].poi_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'neighborhood_pois[0].poi_name', defaultValue: '' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 0,
    fieldMappings: { image: 'poi_featured_image', alt: 'poi_name' },
  },
};

const communityPhotoDef: SlotGroupDefinition = {
  label: 'Photo',
  description: 'Until a photo is picked, shows a gallery photo of a lounge, clubhouse or coffee bar (where residents meet), else the featured image.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: '' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const petsPhotoDef: SlotGroupDefinition = {
  label: 'Photo',
  description: 'Until a photo is picked, shows a gallery photo of a dog park or pets, else a community photo, else the featured image.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: '' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const communityCategoryPhotoDef: SlotGroupDefinition = {
  label: 'Photo',
  description: 'Until a photo is picked, shows the first gallery photo in the Community category, else the featured image.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: '' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const PHOTO_DEFS: Record<PhotoStrategy, SlotGroupDefinition> = {
  featured: photoDef,
  amenity: amenityPhotoDef,
  park: parkPhotoDef,
  community: communityPhotoDef,
  'community-category': communityCategoryPhotoDef,
  pets: petsPhotoDef,
};

const ctaDef: SlotGroupDefinition = {
  label: 'Button',
  description: "Reviews page only. Until edited, links to the Google Business profile, else the Google place page; hidden when the property has neither.",
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Read Every Review on Google' },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_business_url',
      suggestedFields: ['property_google_business_url', 'property_google_place_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

type PhotoStrategy = 'featured' | 'amenity' | 'park' | 'community' | 'community-category' | 'pets';
type LinkRequirement = 'schools' | 'around' | 'siblings' | 'pets' | 'homes-photos' | 'community-photos' | 'neighborhood-photos';

interface JumpLink {
  label: string;
  href: string;
  requires?: LinkRequirement;
}

interface PageHeroProps {
  tokenPrefix?: string;
  /** Eyebrow before the dot; the community name or city follows it. */
  eyebrow?: string;
  eyebrowSuffix?: 'name' | 'city' | 'none';
  /** Heading when the blurb has no usable bold sentence. */
  title?: string;
  /** The page blurb whose bold first sentence is the heading and the rest of paragraph 1 the lead. */
  blurbField?: string;
  photo?: PhotoStrategy;
  /** No photo on this page; the copy widens to fill the hero. */
  hidePhoto?: boolean;
  links?: JumpLink[];
  /** Lead for pages without a blurb field; "{community}" becomes a property name field token. */
  lead?: string;
  /** "google-reviews": an outlined button under the lead to the property's Google reviews (leaves the site). */
  cta?: 'google-reviews';
  /** Heading and lead when the property is not pet friendly; the blurb is ignored. "{community}" becomes a property name field token. */
  noPets?: { title: string; lead: string };
}


interface Photo {
  image?: string;
  alt_text?: string;
  title?: string;
}

const GREEN = /^(park|city_park|state_park|national_park|garden|botanical_garden|nature_preserve|hiking_area|playground)$/;

const poiCaption = (poi: Poi) => `${poi.poi_name}${poi.poi_miles_from_property ? ` · ${poi.poi_miles_from_property}` : ''}`;

function pickPhoto(strategy: PhotoStrategy, photos: Photo[], pois: Poi[], featured: string | null): { src: string; alt: string; caption: string } | null {
  if (strategy === 'amenity') {
    const g = photos.find((x) => /pool|clubhouse/i.test(x.alt_text || ''));
    if (g?.image) return { src: g.image, alt: g.alt_text || '', caption: '' };
    if (featured) return { src: featured, alt: '', caption: '' };
    return photos[0]?.image ? { src: photos[0].image, alt: photos[0].alt_text || '', caption: '' } : null;
  }
  if (strategy === 'community') {
    const g = photos.find((x) => /lounge|clubhouse|club house|coffee bar|community/i.test(x.alt_text || ''));
    if (g?.image) return { src: g.image, alt: g.alt_text || '', caption: '' };
    if (featured) return { src: featured, alt: '', caption: '' };
    return photos[0]?.image ? { src: photos[0].image, alt: photos[0].alt_text || '', caption: '' } : null;
  }
  if (strategy === 'community-category') {
    const g = photos.find((x) => x.title === 'Community');
    if (g?.image) return { src: g.image, alt: g.alt_text || '', caption: '' };
    return featured ? { src: featured, alt: '', caption: '' } : null;
  }
  if (strategy === 'pets') {
    const g = photos.find((x) => /dog park|dog run|paw park|\bpets?\b|\bdogs?\b|\bcats?\b/i.test(x.alt_text || '')) || photos.find((x) => x.title === 'Community');
    if (g?.image) return { src: g.image, alt: g.alt_text || '', caption: '' };
    if (featured) return { src: featured, alt: '', caption: '' };
    return photos[0]?.image ? { src: photos[0].image, alt: photos[0].alt_text || '', caption: '' } : null;
  }
  if (strategy === 'park') {
    const withImg = pois.filter((x) => clean(x.poi_featured_image));
    const parks = withImg.filter((x) => x.poi_type === 'Nature & Outdoor Attractions').sort(byDist);
    const notDog = (x: Poi) => !/dog|paw/i.test(x.poi_name || '') && x.poi_place_primary_type !== 'dog_park';
    const poi =
      parks.find((x) => GREEN.test(x.poi_place_primary_type || '') && notDog(x)) ||
      parks.find((x) => /park|garden|preserve|trail/i.test(x.poi_name || '') && notDog(x)) ||
      parks[0] ||
      withImg.slice().sort(byDist)[0];
    return poi ? { src: poi.poi_featured_image!, alt: poi.poi_name || '', caption: poiCaption(poi) } : null;
  }
  const src = featured || photos[0]?.image;
  return src ? { src, alt: photos[0]?.alt_text || '', caption: '' } : null;
}

interface BlurbTokenProps {
  path: string;
  as: 'h1' | 'p';
  className: string;
  /** The page blurb field; omitted when this part of the blurb is unusable or the blurb is empty. */
  field?: string;
  fieldValue: unknown;
  /** The part of the blurb shown while the token still holds the whole blurb. */
  part: string;
  fallback?: string;
}

/**
 * A heading or lead mapped to the page blurb field, so the editor shows where it comes from.
 * While the token still holds that blurb only `part` shows; any other value renders as written.
 */
function BlurbToken({ path, as, className, field, fieldValue, part, fallback }: BlurbTokenProps) {
  const value = useContentToken(path, { defaultField: field, defaultValue: fallback });
  if (field && typeof fieldValue === 'string' && value === fieldValue) {
    return (
      <EditableToken path={path} type="richtext" defaultField={field} defaultValue={fallback} as={as} className={className}>
        {part}
      </EditableToken>
    );
  }
  return <RichTextContent path={path} as={as} className={className} defaultField={field} defaultValue={fallback} />;
}

export function PageHero({
  tokenPrefix,
  eyebrow,
  eyebrowSuffix = 'name',
  title: titleProp,
  blurbField: blurbProp,
  photo = 'featured',
  hidePhoto = false,
  links = [],
  lead: leadPropIn,
  cta: ctaKind,
  noPets,
}: PageHeroProps) {
  const prefix = tokenPrefix || 'pagehero';
  const eyebrowPath = `${prefix}.eyebrow`;
  const titlePath = `${prefix}.title`;
  const leadPath = `${prefix}.lead`;
  const photoPath = `${prefix}.photo`;
  const ctaPath = `${prefix}.cta`;

  const p = useRawProperty();
  const { companyData } = useVibesites();
  const petsOff = !!noPets && !p.property_ispetfriendly;
  const title = petsOff ? noPets.title : titleProp;
  const blurbField = petsOff ? undefined : blurbProp;
  const leadProp = petsOff ? noPets.lead : leadPropIn;
  const suffix =
    eyebrowSuffix === 'city'
      ? `${fieldToken('property_address_city')}, ${fieldToken('property_address_state')}`
      : eyebrowSuffix === 'name'
        ? fieldToken('property_name')
        : '';
  const eyebrowDefault = `<p>${escapeHtml(eyebrow || 'Explore')}${suffix ? ` · ${suffix}` : ''}</p>`;

  const photoDefForPage = PHOTO_DEFS[photo];
  const ph = useSlotGroup(photoPath, photoDefForPage);
  const cta = useSlotGroup(ctaPath, ctaDef);
  const ctaHref = cta.url || clean(p.property_google_business_url) || clean(p.property_google_place_url);

  const fieldValue = blurbField ? p[blurbField] : undefined;
  const firstPara = paragraphs(typeof fieldValue === 'string' ? fieldValue : '')[0] || '';
  const parsed = splitBlurb(firstPara);
  const blurbUsable = !!parsed.title && parsed.title.length <= 90;
  // Until edited: the blurb's bold first sentence is the heading and the rest of paragraph 1 the lead;
  // a blurb with no usable bold sentence keeps the page's own heading and becomes the lead whole.
  const heading = blurbUsable ? noPeriod(parsed.title) : title || 'Page Title';
  const fromBlurb = blurbUsable ? parsed.body : `${parsed.title} ${parsed.body}`.trim();
  const withName = (t: string) => `<p>${escapeHtml(t).replace(/\{community\}/g, fieldToken('property_name'))}</p>`;
  const titleFallback = withName(title || 'Page Title');
  const leadFallback = leadProp ? withName(leadProp) : undefined;
  const leadFallbackText = leadProp ? leadProp.replace(/\{community\}/g, clean(p.property_name) || 'the community') : '';

  const photos = galleryPhotos<Photo>(p.photo_gallery);
  const pois = (p.neighborhood_pois as Poi[]) || [];
  const featured = clean(p.property_featured_image);
  const defaultSrc = photo === 'park' ? clean(pois[0]?.poi_featured_image) : photo === 'amenity' || photo === 'community' || photo === 'community-category' || photo === 'pets' ? photos[0]?.image : featured;
  const isDefaultPhoto = !ph.image || ph.image === defaultSrc;
  const pick = isDefaultPhoto ? pickPhoto(photo, photos, pois, featured) : null;
  const img = pick ? pick.src : ph.image;
  const alt = pick ? (photo === 'featured' && pick.src === featured ? ph.alt || '' : pick.alt) : ph.alt || '';
  const pickedPoi = !pick && photo === 'park' ? pois.find((x) => x.poi_name && clean(x.poi_featured_image) === ph.image) : undefined;
  const caption = pick ? pick.caption : pickedPoi ? poiCaption(pickedPoi) : '';
  const meets = (r: LinkRequirement) => {
    if (r === 'schools') return pois.some((x) => x.poi_type === 'Education');
    if (r === 'around') return pois.some((x) => x.poi_type && x.poi_type !== 'Education');
    if (r === 'siblings') return nearbySiblings(p, companyData?.properties).length > 0;
    if (r === 'homes-photos') return photoTiles(photos).some((t) => t.cat === 'homes');
    if (r === 'community-photos') return photoTiles(photos).some((t) => t.cat === 'community');
    if (r === 'neighborhood-photos') return placeTiles(pois).length > 0;
    return !!p.property_ispetfriendly;
  };
  const shownLinks = links.filter((l) => !l.requires || meets(l.requires));

  return (
    <section className="page-hero">
      <div className="page-hero__copy" data-reveal>
        <RichTextContent path={eyebrowPath} as="div" className="eyebrow" defaultValue={eyebrowDefault} />
        <BlurbToken
          path={titlePath}
          as="h1"
          className="h1"
          field={blurbUsable ? blurbField : undefined}
          fieldValue={fieldValue}
          part={heading}
          fallback={titleFallback}
        />
        <BlurbToken
          path={leadPath}
          as="p"
          className="page-hero__lead"
          field={firstPara ? blurbField : undefined}
          fieldValue={fieldValue}
          part={fromBlurb || leadFallbackText}
          fallback={leadFallback}
        />
        {shownLinks.length > 0 && (
          <div className="page-hero__links">
            {shownLinks.map((l) => (
              <a key={l.href} className="textlink" href={l.href}>
                {l.label} ↓
              </a>
            ))}
          </div>
        )}
        {ctaKind === 'google-reviews' && (
          <EditableSlotGroup groupPath={ctaPath} definition={ctaDef} className="flex pt-2 empty:hidden">
            {ctaHref && (
              <a className="btn btn-secondary" href={ctaHref} target={cta._target || '_blank'} rel="noopener">
                {cta.label || 'Read Every Review on Google'} <span className="btn__arrow">→</span>
              </a>
            )}
          </EditableSlotGroup>
        )}
      </div>
      {hidePhoto ? (
        <div className="page-hero__photo--empty" />
      ) : (
        <EditableSlotGroup groupPath={photoPath} definition={photoDefForPage} className={img ? 'photo page-hero__photo' : 'page-hero__photo--empty'}>
          {img && <VibesitesImage url={img} alt={alt} priority sizes="(min-width: 1200px) 55vw, 100vw" />}
          {img && caption && <div className="pill pill--paper photo__caption">{caption}</div>}
        </EditableSlotGroup>
      )}

      <EditableToken path="pagehero.eyebrow" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <EditableToken path="pagehero.title" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <EditableToken path="pagehero.lead" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <EditableSlotGroup groupPath="pagehero.photo" definition={photoDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="pagehero.cta" definition={ctaDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageHero',
  displayName: 'Page Hero',
  tokens: {
    'pagehero.eyebrow': {
      label: 'Eyebrow',
      description: 'The page name, then the community name (the city on the Neighborhood page).',
      type: 'richtext',
      defaultValue: '<p>Explore · <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'pagehero.title': {
      label: 'Title',
      description: "Mapped to this page's blurb field, showing only its bold first sentence. Type over it to replace it, or pick another field to show that field in full.",
      type: 'richtext',
      defaultValue: '<p>Page Title</p>',
      suggestedFields: [
        'property_amenities_blurb',
        'property_gallery_blurb',
        'property_neighborhood_blurb',
        'property_virtualtours_blurb',
        'property_reviews_blurb',
        'property_pets_blurb',
        'property_contactus_blurb',
        'property_interactivemap_blurb',
      ],
    },
    'pagehero.lead': {
      label: 'Lead',
      description: "Mapped to this page's blurb field, showing the rest of its first paragraph. Type over it to replace it, or pick another field to show that field in full.",
      type: 'richtext',
      defaultValue: '',
      suggestedFields: [
        'property_amenities_blurb',
        'property_gallery_blurb',
        'property_neighborhood_blurb',
        'property_virtualtours_blurb',
        'property_reviews_blurb',
        'property_pets_blurb',
        'property_contactus_blurb',
        'property_interactivemap_blurb',
      ],
    },
  },
  slotGroups: {
    'pagehero.photo': photoDef,
    'pagehero.cta': ctaDef,
  },
};

(PageHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PageHero;
